import { useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Linkedin, Instagram } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { openCookieSettings } from "@/lib/consent";
import { INK_DEEP } from "@/lib/marketing-theme";

/**
 * The public site's footer, exactly as the website draft has it: one credential
 * line and four links above a hairline, on the same near-black ground as each
 * page's closing CTA band — so the two read as a single band, which is how the
 * draft draws it (the footer row lives *inside* the CTA block there).
 *
 * The site's only footer: no sitemap, no second legal row. `Footer.tsx` renders
 * it under every page except admin, which draws its own chrome.
 */
const LINK_COLOR = "rgba(247,243,236,0.78)";
const MUTED_ON_DARK = "rgba(247,243,236,0.6)";
const HAIRLINE = "rgba(255,255,255,0.14)";
/** Company name and registered address — the same in every language. */
const CREDENTIAL_LINE = "Coliviq GmbH · Elvirastraße 10, 80636 München";
/** INK_DEEP as the browser reports it, for comparing a neighbour's background. */
const INK_DEEP_RGB = "rgb(6, 32, 31)";

const MarketingFooter = () => {
  const { t, language } = useLanguage();
  const de = language === "de";
  const { pathname } = useLocation();
  const ref = useRef<HTMLElement>(null);

  // Is this footer the BOTTOM OF A BAND, or the whole band?
  //
  // The draft draws the link row inside each page's closing CTA block, which is
  // why the row carries a hairline above it — a divider between the CTA and the
  // links, both on INK_DEEP. But only some marketing pages have a closing block
  // (/, /owners, /developers, /careers). On the ones that don't — /community,
  // /contact, /concept, the room lists, and the owner signup flow — that
  // hairline sits at the very top of the dark area with nothing above it, so it
  // reads as a broken edge rather than a divider, and the strip butts straight
  // against the pale page.
  //
  // Detected rather than listed: a list of "pages with a closing block" is a
  // second copy of something the page components already know, and it would go
  // stale the moment a page gains or loses one — which is exactly the bug that
  // put the signup flow on the app footer in the first place.
  //
  // The footer's previousElementSibling is the ROUTE WRAPPER, not the closing
  // block — the page renders inside it — so the block is found by walking DOWN
  // the last-child chain. Bounded, because the answer is only ever a few levels
  // in and an unbounded walk would start finding dark things that are not a
  // closing band.
  const [continuesBand, setContinuesBand] = useState(false);
  useLayoutEffect(() => {
    let node = ref.current?.previousElementSibling ?? null;
    let found = false;
    for (let depth = 0; node && depth < 6; depth++) {
      if (window.getComputedStyle(node).backgroundColor === INK_DEEP_RGB) {
        found = true;
        break;
      }
      node = node.lastElementChild;
    }
    setContinuesBand(found);
  }, [pathname]);

  const links: { label: string; to?: string; onClick?: () => void }[] = [
    // Same mapping as the nav, and just as easy to get backwards:
    // Easy to get backwards, and it was: COL-464 left /owners serving the
    // management pitch (Operators) and /developers the development one
    // (Owners), so both links pointed at /developers and "WG-Vermieter"
    // opened the Projektpartner page. The nav has always had this right.
    { label: de ? "WG-Vermieter" : "For landlords", to: "/owners" },
    { label: de ? "Projektpartner" : "Project partners", to: "/developers" },
    { label: t("footer.careers", de ? "Karriere" : "Careers"), to: "/careers" },
    {
      label: t("footer.privacy", de ? "Datenschutz" : "Privacy"),
      to: "/privacy-policy",
    },
    {
      label: t("footer.imprint", de ? "Impressum" : "Imprint"),
      to: "/imprint",
    },
    { label: t("footer.terms", "AGB"), to: "/terms-of-service" },
    {
      label: t("footer.cookieSettings", de ? "Cookies" : "Cookies"),
      onClick: openCookieSettings,
    },
  ];

  return (
    <footer
      ref={ref}
      /* The assist widget floats at bottom-6 right-6 (24px in, ~44px tall) and
         was covering Impressum and Cookies. The footer ends above it instead of
         trying to guess its width — that guess breaks as soon as the label is
         translated, and the widget is anchored to the viewport, not to us. */
      className="px-5 pb-[6.5rem] sm:px-8 lg:px-14"
      style={{
        background: INK_DEEP,
        // Inline rather than a utility class: with no CTA block above, the
        // strip needs its own top space, and this is the one value that has to
        // survive regardless of what else in the codebase happens to use the
        // same Tailwind step.
        paddingTop: continuesBand ? undefined : "3.5rem",
      }}
    >
      <div
        className={`mx-auto flex max-w-[1328px] flex-col gap-5 sm:flex-row sm:items-center sm:justify-between sm:gap-10 ${
          continuesBand ? "border-t pt-7" : ""
        }`}
        style={continuesBand ? { borderColor: HAIRLINE } : undefined}
      >
        <span className="text-[13.5px]" style={{ color: MUTED_ON_DARK }}>
          {CREDENTIAL_LINE}
        </span>
        <nav className="flex flex-wrap items-center gap-x-[26px] gap-y-2">
          {links.map((link) =>
            link.to ? (
              <Link
                key={link.label}
                to={link.to}
                className="text-[13.5px] transition-opacity hover:opacity-70"
                style={{ color: LINK_COLOR }}
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.label}
                type="button"
                onClick={link.onClick}
                className="text-[13.5px] transition-opacity hover:opacity-70"
                style={{ color: LINK_COLOR }}
              >
                {link.label}
              </button>
            )
          )}
          {/* The draft divides the socials off from the text links. */}
          <span
            className="flex items-center gap-3.5 pl-2.5"
            style={{ borderLeft: `1px solid rgba(247,243,236,0.2)` }}
          >
            <a
              href="https://www.linkedin.com/company/coliviq"
              target="_blank"
              rel="noreferrer noopener"
              aria-label={de ? "coliviq auf LinkedIn" : "coliviq on LinkedIn"}
              className="inline-flex transition-opacity hover:opacity-70"
              style={{ color: LINK_COLOR }}
            >
              <Linkedin className="h-[18px] w-[18px]" strokeWidth={2} />
            </a>
            <a
              href="https://www.instagram.com/coliviq"
              target="_blank"
              rel="noreferrer noopener"
              aria-label={de ? "coliviq auf Instagram" : "coliviq on Instagram"}
              className="inline-flex transition-opacity hover:opacity-70"
              style={{ color: LINK_COLOR }}
            >
              <Instagram className="h-[18px] w-[18px]" strokeWidth={2} />
            </a>
          </span>
        </nav>
      </div>
    </footer>
  );
};

export default MarketingFooter;
